/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import ToolbarPlugin from '@/components/lexical-plugins/ToolbarPlugin';
import ContextMenuPlugin from '@/components/lexical-plugins/ContextMenuPlugin';
import {
    $convertFromMarkdownString,
    $convertToMarkdownString,
    TRANSFORMERS,
} from '@lexical/markdown';

import LexicalTheme from './LexicalTheme';
import '@/components/lexical-plugins/index.css'
import './LexicalEditor.css'

import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HashtagNode } from '@lexical/hashtag';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $getSelection, SELECTION_CHANGE_COMMAND } from 'lexical';
import { useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { useStore } from './store';
import { useShallow } from 'zustand/react/shallow';
import { useDispatch, useSelector } from 'react-redux';
import { CONTINUE_EDIT_CONTENT_WITH_MARKDOWN, MARKDOWN_EDITOR_DIALOG, SHOW_SIDEBAR_WITH_AIFLOW } from '@/constants/actionTypes';
import { useLocation } from "react-router-dom";

const Nodes = [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    HashtagNode,
    CodeHighlightNode,
    AutoLinkNode,
    LinkNode,
    OverflowNode,
    HorizontalRuleNode,
    MarkNode,
];

function Placeholder() {
    return <div className="editor-placeholder">Type note here</div>;
}

// 新增：编辑器刷新插件
function EditorRefreshPlugin({ content, refresher }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (refresher !== undefined) {
            editor.update(() => {
                $convertFromMarkdownString(content || '', TRANSFORMERS);
            });
        }
    }, [refresher, editor, content]);

    return null;
}

function EditablePlugin({ id }) {
    const [editor] = useLexicalComposerContext();
    const markdownEditorState = useSelector(state => state.uiState.mdEditorDialog) || {};

    useEffect(() => {
        editor.setEditable(!markdownEditorState?.visible || markdownEditorState?.nodeId !== id);
    }, [markdownEditorState?.visible, markdownEditorState?.nodeId]);

    return null;
}

export default function App({ id, value, onChange, color_theme, hovered, getSelectionText, onContextMenu, isMobile, refresher }) {
    const { setContextMenu } = useStore(useShallow(state => ({
        setContextMenu: state.setContextMenu,
    })));

    const dispatch = useDispatch();

    const [content, setContent] = useState(value);
    const continueWithMarkdown = useSelector(state => state.uiState.continueWithMarkdown);
    const location = useLocation();
    const params = new Proxy(new URLSearchParams(location.search), {
        get: (searchParams, prop) => searchParams.get(prop) || '',
    });

    useEffect(() => {
        if (continueWithMarkdown === id) {
            dispatch({
                type: MARKDOWN_EDITOR_DIALOG,
                value: {
                    app: 'aiflow',
                    visible: true,
                    hid: params.hid,
                    nodeId: id,
                    content,
                    updateId: undefined
                }
            });

            dispatch({
                type: SHOW_SIDEBAR_WITH_AIFLOW,
                value: false
            })

            dispatch({
                type: CONTINUE_EDIT_CONTENT_WITH_MARKDOWN,
                value: null
            })
        }
    }, [continueWithMarkdown])

    function onEditorChange(editorState) {
        editorState.read(() => {
            const markdown = $convertToMarkdownString(TRANSFORMERS);
            setContent(markdown);
        });
    }

    useEffect(() => {
        !hovered && value !== content && onChange(content);
    }, [hovered])

    return (
        <LexicalComposer
            initialConfig={{
                namespace: id,
                nodes: Nodes,
                editorState: () => $convertFromMarkdownString(content || '', TRANSFORMERS),
                onError(error) {
                    throw error;
                },
                theme: LexicalTheme
            }}
        >
            <div
                className={isMobile ? "editor-container" : "editor-container nodrag"}
                style={{
                    backgroundColor: color_theme.content_bg
                }}
            >
                <div style={{
                    position: 'absolute',
                    bottom: 'calc(100%)',
                    left: -6
                }}>
                    <ToolbarPlugin setIsLinkEditMode={true} visible={hovered} />
                </div>
                <div className="editor-inner">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="editor-input"
                                onMouseUp={(event) => {
                                    const text = getSelectionText();

                                    if (text) {
                                        onContextMenu({ type: 'text_selection', event, selected_text: text, nodeId: id, setContextMenu })
                                    }
                                }}
                                onMouseDown={(event) => {
                                    window.getSelection().removeAllRanges();
                                    setContextMenu(null);
                                }}
                                onClick={(event) => {
                                    const text = getSelectionText();

                                    if (text) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        }
                        placeholder={<Placeholder />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <OnChangePlugin onChange={onEditorChange} ignoreSelectionChange={true} />
                    <EditorRefreshPlugin content={value} refresher={refresher} />
                    <EditablePlugin id={id} continueWithMarkdown={continueWithMarkdown} />
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
                    <ListPlugin />
                    <CheckListPlugin />
                    <LinkPlugin />
                    <ContextMenuPlugin />
                    <HorizontalRulePlugin />
                    <TabIndentationPlugin />
                </div>
            </div>
        </LexicalComposer>
    );
}